/**
 * One-time move when parties became company-specific:
 * - companyIds: every company that has invoices / purchase bills with the party (none yet → all companies)
 * - openingBalances: the hand-typed part of the old single due (due − open bills) goes to the default
 *   company if the party is ticked for it, else to its first company.
 * Runs once after login (flag settings/migrations.partyCompanies); parties that already have
 * companyIds are left alone, so running it twice changes nothing.
 */
import { doc, getDoc, setDoc, writeBatch, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase/firestore.js";
import { auth } from "../../../firebase/auth.js";
import { writeAudit } from "../../../firebase/auditLogger.js";
import { customerStore } from "../hooks/useCustomers.js";
import { invoiceStore } from "../../invoices/hooks/useInvoices.js";
import { purchaseStore } from "../../purchases/hooks/usePurchases.js";
import { UNASSIGNED_COMPANY, getRecordCompanyKey } from "../../../shared/utils/companyScope.js";

const FLAG_DOC = () => doc(db, "settings", "migrations");
const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;
const lower = (v) => String(v || "").trim().toLowerCase();
const balanceOf = (b) => {
  const total = Number(b.grandTotal || 0);
  const paid = Number(b.paid !== undefined ? b.paid : (b.paidAmount || 0));
  return Number(b.balance !== undefined ? b.balance : Math.max(0, total - paid));
};

/** Works out companyIds / openingBalances for parties that don't have them yet. Pure — used by the app and tests. */
export function planPartyCompanies({ parties, invoices, purchases, companies, defaultCompanyId }) {
  const companyKey = (bill) => {
    const key = getRecordCompanyKey(bill, companies);
    return key === UNASSIGNED_COMPANY ? defaultCompanyId : key;
  };
  const allIds = companies.map((c) => c.id);
  const updates = [];
  parties.forEach((p) => {
    if (Array.isArray(p.companyIds) && p.companyIds.length) return;
    const name = lower(p.name);
    const mine = (b, idField, nameField) => (b[idField] && b[idField] === p.id) || (name && lower(b[nameField]) === name);
    const sales = invoices.filter((b) => mine(b, "customerId", "customerName"));
    const buys = purchases.filter((b) => mine(b, "vendorId", "vendorName"));
    const used = [...new Set([...sales, ...buys].map(companyKey).filter(Boolean))];
    const companyIds = used.length ? allIds.filter((id) => used.includes(id)) : allIds;

    // Old single due minus what open bills explain = opening due typed in by hand
    const type = p.partyType || "customer";
    const fromBills = (type !== "vendor" ? sales.reduce((s, b) => s + balanceOf(b), 0) : 0)
      + (type === "vendor" || type === "both" ? buys.reduce((s, b) => s + balanceOf(b), 0) : 0);
    const handTyped = round2(Number(p.openingBalance || 0) - fromBills);
    const openingBalances = { ...(p.openingBalances || {}) };
    if (handTyped > 0.01) {
      const target = companyIds.includes(defaultCompanyId) ? defaultCompanyId : companyIds[0];
      openingBalances[target] = round2(Number(openingBalances[target] || 0) + handTyped);
    }
    updates.push({ id: p.id, companyIds, openingBalances });
  });
  return updates;
}

let running = null;

/** Called after login once companies have loaded. Safe to call many times. */
export function ensurePartyCompanies({ companies, defaultCompany }) {
  if (running || !companies?.length) return running;
  running = (async () => {
    const flag = await getDoc(FLAG_DOC());
    if (flag.exists() && flag.data()?.partyCompanies) return { skipped: true };

    const [parties, invoices, purchases] = await Promise.all([customerStore.load(), invoiceStore.load(), purchaseStore.load()]);
    const updates = planPartyCompanies({ parties, invoices, purchases, companies, defaultCompanyId: defaultCompany?.id || companies[0].id });
    for (let i = 0; i < updates.length; i += 400) {
      const batch = writeBatch(db);
      updates.slice(i, i + 400).forEach((u) => batch.update(doc(db, "customers", u.id), {
        companyIds: u.companyIds,
        openingBalances: u.openingBalances,
        updatedAt: serverTimestamp()
      }));
      await batch.commit();
    }
    const by = auth.currentUser?.email || auth.currentUser?.uid || "admin";
    await setDoc(FLAG_DOC(), { partyCompanies: { at: new Date().toISOString(), by, count: updates.length } }, { merge: true });
    writeAudit("PARTIES_ASSIGNED_TO_COMPANIES", { count: updates.length });
    if (updates.length) await customerStore.refresh();
    return { updated: updates.length };
  })().catch((err) => {
    console.error("Assigning parties to companies failed (will retry next time):", err);
    running = null;
    return { error: err.message };
  });
  return running;
}
